import type { FrameworkConfig, ProjectConfig } from "../../types/types.js"
import { logger } from "../../utils/logger.js"
import { BaseFramework } from "../base-framework.js"

const astroConfig: FrameworkConfig = {
  defaultMeta: "none",
  displayName: "Astro",
  metaFrameworks: ["none"],
  name: "astro",
  starter: {
    args: (config: ProjectConfig) => {
      const args: Array<string> = []
      args.push("--skip-install")
      if (config.typescript) {
        args.push("--typescript", "strict")
      } else {
        args.push("--no-typescript")
      }
      args.push("--template", "minimal")
      args.push("--no-git")
      return args
    },
    command: "create-astro",
  },
}

export class AstroFramework extends BaseFramework {
  constructor() {
    super(astroConfig)
  }

  getStarterCommand(projectConfig: ProjectConfig): string {
    const { projectName, typescript } = projectConfig
    const args = [
      projectName,
      "--no-install",
      typescript ? "--typescript strict" : "--no-typescript",
      "--template minimal",
      "--no-git",
      "--skip-houston", // Skip the Houston animation
    ]

    return `npm create astro@latest ${args.join(" ")}`
  }

  getPostInstallTasks(
    projectConfig: ProjectConfig,
  ): Array<() => Promise<void>> {
    const tasks: Array<() => Promise<void>> = []

    tasks.push(async () => {
      // Astro specific setup
      await this.updatePackageJson(projectConfig, {
        scripts: {
          astro: "astro",
          build: "astro build",
          dev: "astro dev",
          preview: "astro preview",
          start: "astro dev",
        },
      })

      // If TypeScript is enabled, ensure proper config
      if (projectConfig.typescript) {
        await this.ensureAstroTypeScriptConfig(projectConfig)
      }
    })

    return tasks
  }

  private async ensureAstroTypeScriptConfig(
    projectConfig: ProjectConfig,
  ): Promise<void> {
    if (projectConfig.dryRun) {
      return
    }

    // Astro automatically creates a tsconfig.json, but we might need to adjust it
    const tsConfigPath = `${projectConfig.projectPath}/tsconfig.json`

    try {
      const { readFileSync, writeFileSync } = await import("fs")
      const tsConfig = JSON.parse(readFileSync(tsConfigPath, "utf-8"))

      // Ensure strict mode is enabled
      tsConfig.compilerOptions = {
        ...tsConfig.compilerOptions,
        forceConsistentCasingInFileNames: true,
        skipLibCheck: true,
        strict: true,
      }

      writeFileSync(tsConfigPath, JSON.stringify(tsConfig, null, 2))
    } catch (error) {
      logger.info(`Error updating TypeScript config: ${error}`)
    }
  }
}
